import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const TABS = [
  { id: "about",   label: "About Us",          icon: "ℹ️",  path: "/about"   },
  { id: "privacy", label: "Privacy Policy",     icon: "🔒", path: "/privacy" },
  { id: "terms",   label: "Terms & Conditions", icon: "📋", path: "/terms"   },
  { id: "refund",  label: "Refund Policy",      icon: "💸", path: "/refund"  },
  { id: "contact", label: "Contact Us",         icon: "📞", path: "/contact" },
];

const styles = `
  @keyframes fadeInUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
  }
  .legal-fade { animation: fadeInUp 0.35s ease both; }
  .legal-tab  { transition: background 0.18s, border-color 0.18s, color 0.18s; cursor:pointer; }
  .legal-tab:hover:not(.legal-tab-active) {
    border-color: #f97316 !important;
    color: #f97316 !important;
  }
  .legal-section h3 {
    border-left: 3px solid #f97316;
    padding-left: 12px;
    color: #f1f5f9;
    font-size: 15px;
    font-weight: 700;
    margin: 28px 0 10px;
    line-height: 1.4;
  }
  .legal-section h2 {
    color: #f1f5f9;
    font-size: 22px;
    font-weight: 800;
    margin-bottom: 4px;
    line-height: 1.3;
  }
  .legal-section p {
    color: #94a3b8;
    font-size: 14px;
    line-height: 1.85;
    margin-bottom: 10px;
  }
  .legal-section ul {
    padding-left: 18px;
    margin-bottom: 12px;
  }
  .legal-section ul li {
    color: #94a3b8;
    font-size: 14px;
    line-height: 1.85;
    margin-bottom: 6px;
  }
  .legal-section strong { color: #f1f5f9; }
  .legal-highlight {
    background: rgba(249,115,22,0.08);
    border: 1px solid rgba(249,115,22,0.25);
    border-radius: 10px;
    padding: 14px 16px;
    margin: 14px 0;
    font-size: 13.5px;
    color: #fdba74;
    line-height: 1.75;
  }
  .legal-contact-box {
    background: #111827;
    border: 1px solid #1e293b;
    border-radius: 12px;
    padding: 16px 20px;
    margin-top: 28px;
    font-size: 13.5px;
    color: #94a3b8;
    line-height: 1.85;
  }
  .legal-contact-box strong { color: #f97316; }
  .legal-email-link {
    color: #f97316;
    text-decoration: none;
  }
  .legal-email-link:hover { text-decoration: underline; }
  .pricing-card {
    background: #0a0a0a;
    border: 1px solid #1e293b;
    border-radius: 12px;
    padding: 16px 18px;
    margin-bottom: 12px;
  }
  .pricing-card-title {
    font-size: 15px;
    font-weight: 700;
    color: #f1f5f9;
    margin-bottom: 4px;
  }
  .pricing-card-amount {
    font-size: 24px;
    font-weight: 800;
    color: #f97316;
    margin-bottom: 6px;
  }
  .pricing-card-desc {
    font-size: 13px;
    color: #64748b;
    line-height: 1.7;
  }
  @media(max-width:640px) {
    .legal-section h2 { font-size: 18px; }
    .legal-section h3 { font-size: 14px; }
    .legal-section p, .legal-section ul li { font-size: 13.5px; }
  }
`;

// ── Content sections ────────────────────────────────────────────────

function AboutContent() {
  return (
    <div className="legal-section">
      <h2>About Us</h2>
      <p style={{ color:"#64748b", fontSize:13, margin:"4px 0 24px" }}>
        PadhaiPass — connecting students with local tutors across India
      </p>

      <p>
        <strong>PadhaiPass</strong> is an online tutor discovery platform that helps students find verified, local home tutors in their city. We make it easy to search, compare, and connect with qualified teachers — without paying hefty commission fees.
      </p>
      <p>
        We are based in <strong>Kanpur, Uttar Pradesh, India</strong> and focused on making quality education accessible at the local level.
      </p>

      <h3>Legal Entity</h3>
      <div className="legal-highlight">
        🏢 <strong>Legal Entity Name:</strong> Bharat Brahm Singh<br />
        <strong>Business Type:</strong> Individual / Sole Proprietor<br />
        <strong>Operating As:</strong> PadhaiPass (padhaipass.com)<br />
        <strong>Address:</strong> Manohar Nagar, Barra-2, Kanpur, Uttar Pradesh, India – 208027
      </div>

      <h3>Our Services &amp; Products</h3>
      <p>PadhaiPass offers two categories of services, all priced in Indian Rupees (INR):</p>

      <div className="pricing-card">
        <div className="pricing-card-title">🎓 Student — Contact Credits</div>
        <div className="pricing-card-amount">₹29</div>
        <div className="pricing-card-desc">
          Students receive <strong>3 free contact credits</strong> on registration.<br />
          Additional credits: <strong>₹29 for 3 credits</strong>.<br />
          Each credit allows the student to unlock a teacher's WhatsApp contact and send a message.<br />
          A 5-minute cooldown applies between consecutive unlocks to prevent misuse.<br />
          Credits are non-transferable and valid until used.
        </div>
      </div>

      <div className="pricing-card">
        <div className="pricing-card-title">👨‍🏫 Teacher — Listing &amp; Platform Fee</div>
        <div className="pricing-card-amount">Free + ₹99–₹299</div>
        <div className="pricing-card-desc">
          Listing your profile on PadhaiPass is <strong>completely free</strong>.<br />
          When a parent books a demo with you, you get their WhatsApp number directly.<br />
          Your profile is then paused. Pay a one-time platform fee to get listed again and receive more students.<br />
          Fee is tiered by your monthly rate: <strong>₹99</strong> (up to ₹1500/mo) · <strong>₹199</strong> (up to ₹3000/mo) · <strong>₹299</strong> (above ₹3000/mo).<br />
          All payments are processed securely through <strong>Cashfree Payments</strong>.
        </div>
      </div>

      <h3>What PadhaiPass Is Not</h3>
      <ul>
        <li>We are <strong>not</strong> a tutoring service — we do not provide or guarantee tutoring sessions</li>
        <li>We are a <strong>technology platform only</strong> connecting students and teachers</li>
        <li>All tutoring arrangements are made directly between the student and teacher</li>
        <li>We do <strong>not</strong> mediate, manage, or take responsibility for offline tutoring sessions</li>
      </ul>

      <h3>Our Mission</h3>
      <p>
        PadhaiPass was built with one goal: make it effortless for any student in a small or large Indian city to find a quality tutor nearby — without relying on word of mouth or overpriced middlemen. We believe every student deserves access to the right teacher.
      </p>

      <h3>Payment Partner</h3>
      <p>
        All payments on PadhaiPass are processed through <strong>Cashfree Payments</strong> (www.cashfree.com), a trusted payment gateway backed by SBI and Y Combinator, used by 800,000+ businesses across India.
      </p>

      <div className="legal-contact-box">
        📧 <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong><br />
        📞 <strong>+91 95699 23598</strong><br />
        📍 Manohar Nagar, Barra-2, Kanpur, Uttar Pradesh – 208027<br /><br />
        <strong>Owner:</strong> Bharat Brahm Singh
      </div>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="legal-section">
      <h2>Privacy Policy</h2>
      <p style={{ color:"#64748b", fontSize:13, margin:"4px 0 24px" }}>
        Effective: March 1, 2026 &nbsp;·&nbsp; Last Updated: April 15, 2026
      </p>

      <p>Welcome to <strong>PadhaiPass</strong> ("we", "our", "us"), operated by <strong>Bharat Brahm Singh</strong> at padhaipass.com. This Privacy Policy explains how we collect, use, and protect your personal information.</p>
      <p>By using PadhaiPass, you agree to the collection and use of information as described in this policy.</p>

      <h3>1. Information We Collect</h3>
      <ul>
        <li><strong>Parents / Students:</strong> Name, email address, phone number, subject and class preferences.</li>
        <li><strong>Teachers:</strong> Name, email address, phone number, location, qualifications, subjects taught, experience, monthly fee, and profile photo.</li>
        <li><strong>Payment Data:</strong> Transaction IDs and order references processed via Cashfree Payments. We do not store card or bank details.</li>
        <li><strong>Usage Data:</strong> Pages visited, search queries, device type, browser type, and IP address.</li>
      </ul>

      <h3>2. How We Use Your Information</h3>
      <ul>
        <li>To connect parents with suitable tutors in their area</li>
        <li>To share contact details between parents and teachers when a demo is booked</li>
        <li>To process payments for teacher platform fees (₹99–₹299 to relist) and parent contact credits (₹29)</li>
        <li>To verify teacher profiles and award the Verified Badge</li>
        <li>To send important updates, notifications, and service-related emails via WhatsApp and email</li>
        <li>To improve our platform and user experience</li>
        <li>To detect and prevent fraud or abuse</li>
      </ul>

      <h3>3. Sharing of Information</h3>
      <p>We do <strong>not</strong> sell your personal data to third parties. We may share your information with:</p>
      <ul>
        <li><strong>Cashfree Payments</strong> to process transactions securely</li>
        <li><strong>Service providers</strong> (hosting, email) who help us operate the platform</li>
        <li><strong>Law enforcement</strong> if required by applicable law</li>
      </ul>
      <div className="legal-highlight">
        ℹ️ <strong>Contact sharing between users:</strong> When a parent books a free demo (spends 1 credit), they receive the teacher's WhatsApp contact to communicate directly. The teacher also receives the parent's name and phone number in their dashboard. By registering on PadhaiPass, both teachers and parents consent to this contact sharing when a demo is booked.
      </div>

      <h3>4. Verified Badge &amp; Profile Data</h3>
      <p>Teachers may be awarded a <strong>Verified Badge</strong> after manual review by the PadhaiPass team. Verification status is stored against the teacher's profile and is visible to students on search and profile pages.</p>

      <h3>5. Data Retention</h3>
      <p>We retain your personal data only for as long as necessary to provide our services and meet legal obligations. Once your account is deleted, your data is removed from active systems within 90 days, except where retention is required by law.</p>

      <h3>6. Data Security</h3>
      <p>We use industry-standard security measures including encrypted connections (HTTPS), rate limiting, input sanitization, and secure token authentication to protect your data.</p>

      <h3>7. Cookies</h3>
      <p>We use cookies to improve user experience, remember preferences, and analyse platform traffic. You can disable cookies in your browser settings, though some features may not work properly.</p>

      <h3>8. Your Rights</h3>
      <ul>
        <li>Request access to your personal data</li>
        <li>Request correction or deletion of your data</li>
        <li>Opt out of promotional communications at any time</li>
      </ul>

      <h3>9. Children's Privacy</h3>
      <p>PadhaiPass is not intended for children under 13. If you are a parent and believe your child has provided us personal information, please contact us immediately.</p>

      <h3>10. Changes to This Policy</h3>
      <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date.</p>

      <div className="legal-contact-box">
        📧 For privacy-related queries, contact us at:{" "}
        <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong><br />
        <strong>Bharat Brahm Singh</strong> · Manohar Nagar, Barra-2, Kanpur, UP – 208027
      </div>
    </div>
  );
}

function TermsContent() {
  return (
    <div className="legal-section">
      <h2>Terms &amp; Conditions</h2>
      <p style={{ color:"#64748b", fontSize:13, margin:"4px 0 24px" }}>
        Effective: March 1, 2026 &nbsp;·&nbsp; Last Updated: March 26, 2026
      </p>

      <p>These Terms and Conditions ("Terms") govern your use of <strong>PadhaiPass</strong> (padhaipass.com), operated by <strong>Bharat Brahm Singh</strong>, Manohar Nagar, Barra-2, Kanpur, Uttar Pradesh. By accessing or using our platform, you agree to be bound by these Terms.</p>

      <h3>1. About PadhaiPass</h3>
      <p>PadhaiPass is a local tutor marketplace that connects students with verified teachers in their area. We are a <strong>technology platform only</strong> — we are not a tutoring service and are not responsible for any offline interaction, arrangement, or outcome between students and teachers.</p>

      <h3>2. User Accounts</h3>
      <ul>
        <li>You must provide accurate and complete information when registering</li>
        <li>You are responsible for maintaining the confidentiality of your account</li>
        <li>You must be at least 16 years old to use this platform</li>
        <li>One person may not maintain multiple accounts</li>
      </ul>

      <h3>3. For Teachers — Free Listing &amp; Lead Unlock</h3>
      <ul>
        <li>Listing your profile on PadhaiPass is <strong>free</strong> — complete your profile to appear in search</li>
        <li>When a parent books a demo, you get their WhatsApp number directly and your profile is paused</li>
        <li>Pay a platform fee (₹99–₹299 based on your monthly rate) to get listed again and receive more students</li>
        <li>By listing, you confirm that all information provided (qualifications, experience, subjects, phone number) is truthful and accurate</li>
        <li>PadhaiPass reserves the right to remove listings that contain false or misleading information</li>
        <li>Teachers may apply for or be awarded a <strong>Verified Badge</strong> upon manual review</li>
      </ul>

      <h3>4. For Students — Contact Credits</h3>
      <div className="legal-highlight">
        🎓 Students receive <strong>3 free contact credits</strong> upon registration.<br />
        Additional credits can be purchased: <strong>₹29 for 3 credits</strong>.<br />
        Each credit allows you to unlock one teacher's WhatsApp contact and send a message.<br />
        A <strong>5-minute cooldown</strong> applies between consecutive unlocks.
      </div>
      <ul>
        <li>Credits are non-transferable and valid until used — they do not expire</li>
        <li>PadhaiPass does not guarantee that a teacher will be available or respond after contact details are unlocked</li>
        <li>Any disputes arising from tutoring arrangements are between the student and teacher directly</li>
        <li>Misuse of the contact system (spam, harassment) will result in immediate account suspension</li>
      </ul>

      <h3>5. Verified Badge</h3>
      <div className="legal-highlight">
        🏅 The <strong>Verified Badge</strong> is awarded to teachers whose profiles have been manually reviewed and confirmed as genuine by the PadhaiPass team.
      </div>
      <ul>
        <li>The badge is a <strong>trust signal only</strong> — it does not guarantee teaching quality, outcomes, or availability</li>
        <li>PadhaiPass reserves the right to revoke the Verified Badge at any time if the profile is found to be inaccurate</li>
        <li>Teachers cannot purchase the Verified Badge — it is awarded solely at the discretion of PadhaiPass</li>
        <li>To request verification, email us at <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong> with subject "Verification Request"</li>
      </ul>

      <h3>6. No Guarantee of Outcomes</h3>
      <div className="legal-highlight">
        ⚠️ PadhaiPass does <strong>not</strong> guarantee:<br />
        &nbsp;&nbsp;• Student academic success or improvement<br />
        &nbsp;&nbsp;• Teacher performance, teaching quality, or availability<br />
        &nbsp;&nbsp;• Class outcomes or results of any kind<br /><br />
        All payments are <strong>final once the service is delivered</strong>. PadhaiPass is solely a technology platform.
      </div>

      <h3>7. Payments</h3>
      <p>All payments are processed securely through <strong>Cashfree Payments</strong>. PadhaiPass does not store your card or bank details. By making a payment, you agree to Cashfree's terms of service. All payments are final once the corresponding digital service is delivered.</p>
      <ul>
        <li>Teacher listing: <strong>Free</strong></li>
        <li>Teacher platform fee (to relist after demo): <strong>₹99 / ₹199 / ₹299</strong> based on monthly rate</li>
        <li>Student contact credits: <strong>₹29 for 3 credits</strong></li>
      </ul>

      <h3>8. Prohibited Activities</h3>
      <ul>
        <li>Posting false or misleading information</li>
        <li>Using the platform for spam or unsolicited communication</li>
        <li>Attempting to bypass the payment or credits system</li>
        <li>Harassing or threatening other users</li>
        <li>Scraping or copying platform data without permission</li>
        <li>Any form of fraud, abuse, or misuse of the platform</li>
      </ul>

      <h3>9. Account Suspension &amp; Fraud</h3>
      <p>We reserve the right to <strong>immediately block or permanently ban</strong> any user found to be involved in fraud, abuse, payment bypass, fake profiles, or any activity that harms other users or the platform. No refund will be issued in such cases.</p>

      <h3>10. Limitation of Liability</h3>
      <p>PadhaiPass acts only as a technology intermediary. We are not responsible for the quality of tutoring, disputes between users, or any loss arising from connections made through the platform. Our maximum liability is limited to the amount paid by you for the specific transaction in question.</p>

      <h3>11. Intellectual Property</h3>
      <p>All content on PadhaiPass including logos, design, and text is owned by Bharat Brahm Singh / PadhaiPass. You may not copy or reproduce any part of the platform without written permission.</p>

      <h3>12. Termination</h3>
      <p>We reserve the right to suspend or terminate any account that violates these Terms without prior notice.</p>

      <h3>13. Governing Law</h3>
      <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in <strong>Kanpur, Uttar Pradesh</strong>.</p>

      <div className="legal-contact-box">
        📧 For questions about these Terms, contact:{" "}
        <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong><br />
        <strong>Bharat Brahm Singh</strong> · Manohar Nagar, Barra-2, Kanpur, UP – 208027<br />
        📞 +91 95699 23598
      </div>
    </div>
  );
}

function RefundContent() {
  return (
    <div className="legal-section">
      <h2>Refund &amp; Cancellation Policy</h2>
      <p style={{ color:"#64748b", fontSize:13, margin:"4px 0 24px" }}>
        Effective: March 1, 2026 &nbsp;·&nbsp; Last Updated: March 26, 2026
      </p>

      <p>At <strong>PadhaiPass</strong>, operated by <strong>Bharat Brahm Singh</strong>, we strive to provide genuine value to both students and teachers. Please read our refund policy carefully before making any payment.</p>

      <div className="legal-highlight">
        ⚠️ All payments on PadhaiPass are <strong>final and non-refundable</strong> once the digital service has been delivered. By making a payment, you acknowledge that you have read and agreed to this policy.
      </div>

      <h3>1. Student Payments — Contact Credits (₹29 for 3 Credits)</h3>
      <p>Students purchase contact credits at <strong>₹29 for 3 credits</strong>. Each credit is used to unlock a teacher's WhatsApp contact.</p>
      <ul>
        <li>Once credits are added to your account, the payment is <strong>non-refundable</strong></li>
        <li>Credits do not expire and can be used at any time</li>
      </ul>
      <p><strong>Refund eligible cases for student payments:</strong></p>
      <ul>
        <li>Payment was deducted but credits were <strong>not added</strong> to your account due to a technical error</li>
        <li>Duplicate payment was charged for the same order</li>
        <li>Teacher profile used was confirmed fake or fraudulent by our team after the unlock</li>
      </ul>

      <h3>2. Teacher Payments — Platform Fee to Relist (₹99 / ₹199 / ₹299)</h3>
      <div className="legal-highlight">
        📋 The platform fee is <strong>non-refundable</strong> once your profile has been relisted and made visible to students again.
      </div>
      <p><strong>Refund eligible cases for teacher payments:</strong></p>
      <ul>
        <li>Payment was deducted but your profile was <strong>not relisted</strong> due to a technical error</li>
        <li>Duplicate payment was made for the same relisting</li>
        <li>Technical error caused a double charge</li>
      </ul>

      <h3>3. How to Request a Refund</h3>
      <p>To request a refund, email us within <strong>7 days</strong> of the transaction with:</p>
      <ul>
        <li>Your registered email or phone number</li>
        <li>Transaction ID or payment receipt from Cashfree</li>
        <li>Reason for refund request</li>
      </ul>
      <p>Send to: <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong></p>

      <h3>4. Refund Processing Time</h3>
      <ul>
        <li>Refund requests are reviewed within <strong>3–5 business days</strong></li>
        <li>Approved refunds are processed within <strong>7–10 business days</strong></li>
        <li>Refund will be credited to the original payment method</li>
      </ul>

      <h3>5. Cancellation Policy</h3>
      <p>There is no subscription or recurring charge on PadhaiPass. Each payment is a one-time transaction. Once a payment is made and the digital service is delivered (credits added / lead unlocked), it cannot be cancelled.</p>

      <h3>6. Free Credits on Registration</h3>
      <p>New student accounts receive <strong>3 free contact credits</strong> upon registration at no charge. These are not refundable in cash as no payment was made for them.</p>

      <div className="legal-contact-box">
        📧 Refund queries:{" "}
        <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong><br />
        📞 <strong>+91 95699 23598</strong><br />
        Please allow 1–2 business days for our team to respond.
      </div>
    </div>
  );
}

function ContactContent() {
  return (
    <div className="legal-section">
      <h2>Contact Us</h2>
      <p style={{ color:"#64748b", fontSize:13, margin:"4px 0 24px" }}>
        We typically respond within 1–2 business days
      </p>

      <p>Have a question, concern, or feedback? We'd love to hear from you. PadhaiPass is built for students and teachers across India — your feedback directly shapes how we grow.</p>

      <div className="legal-highlight">
        🏢 <strong>Legal Entity:</strong> Bharat Brahm Singh (Sole Proprietor)<br />
        🌐 <strong>Operating As:</strong> PadhaiPass · padhaipass.com
      </div>

      <h3>📞 Phone</h3>
      <p>
        <strong><a href="tel:+919569923598" className="legal-email-link">+91 95699 23598</a></strong><br />
        Monday to Saturday, 10:00 AM – 6:00 PM IST
      </p>

      <h3>📧 Email</h3>
      <p>
        <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong><br />
        For general queries, support, refund requests, or reporting fake profiles.
      </p>

      <h3>📍 Operating Address</h3>
      <p>
        <strong>Bharat Brahm Singh</strong><br />
        Manohar Nagar, Barra-2<br />
        Kanpur, Uttar Pradesh – 208027<br />
        India
      </p>

      <h3>🌐 Website</h3>
      <p><strong>padhaipass.com</strong></p>

      <h3>⏰ Support Hours</h3>
      <p>
        Monday to Saturday: 10:00 AM – 6:00 PM IST<br />
        Closed on Sundays and national holidays.
      </p>

      <h3>🏅 Request Verified Badge</h3>
      <p>Are you a teacher on PadhaiPass and want your profile to be verified? Email us at{" "}
        <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong>{" "}
        with the subject line <strong>"Verification Request"</strong> and include your registered email or phone number. Our team will review your profile and respond within 2–3 business days.
      </p>
      <p style={{ color:"#64748b", fontSize:13 }}>Note: The Verified Badge is awarded at the sole discretion of PadhaiPass after manual review. It cannot be purchased.</p>

      <h3>🚨 Report a Fake Profile</h3>
      <p>If you come across a fake or misleading teacher profile, please email us immediately at{" "}
        <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong>{" "}
        with the subject line <strong>"Report Fake Profile"</strong>. We take all reports seriously and act within 24 hours.
      </p>

      <div className="legal-highlight">
        💬 For the fastest response, email or WhatsApp us with your registered phone number or email so we can locate your account quickly.
      </div>

      <div className="legal-contact-box">
        📧 <strong><a href="mailto:leadpadhaipass@gmail.com" className="legal-email-link">leadpadhaipass@gmail.com</a></strong><br />
        📞 <strong><a href="tel:+919569923598" className="legal-email-link">+91 95699 23598</a></strong><br />
        📍 Manohar Nagar, Barra-2, Kanpur, Uttar Pradesh – 208027<br /><br />
        We're a small team building something meaningful for students and teachers across India. Every message matters to us. 🙏
      </div>
    </div>
  );
}

const CONTENT_MAP = {
  about:   AboutContent,
  privacy: PrivacyContent,
  terms:   TermsContent,
  refund:  RefundContent,
  contact: ContactContent,
};

// ── Main component ──────────────────────────────────────────────────
export default function Legal({ tab = "about" }) {
  const nav      = useNavigate();
  const location = useLocation();

  const activeTab = TABS.find(t => t.id === tab) ? tab : "about";
  const Content   = CONTENT_MAP[activeTab];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  return (
    <div style={{ background:"#0a0a0a", minHeight:"100vh", color:"#f1f5f9" }}>
      <style>{styles}</style>

      {/* ── Hero banner ── */}
      <div style={{
        background: "linear-gradient(180deg, #111 0%, #0a0a0a 100%)",
        borderBottom: "1px solid #1e293b",
        padding: "28px 20px 0",
        textAlign: "center",
      }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:"#f1f5f9", margin:"0 0 4px" }}>
          Legal &amp; Policies
        </h1>
        <p style={{ color:"#475569", fontSize:13, margin:"0 0 20px" }}>
          padhaipass.com · All policies in one place
        </p>

        {/* ── Tab bar ── */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: 8,
          flexWrap: "wrap",
          paddingBottom: 0,
        }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => nav(t.path)}
              className={`legal-tab${activeTab === t.id ? " legal-tab-active" : ""}`}
              style={{
                padding: "9px 16px",
                borderRadius: 999,
                border: `2px solid ${activeTab === t.id ? "#f97316" : "#1e293b"}`,
                background: activeTab === t.id ? "#f97316" : "transparent",
                color: activeTab === t.id ? "#fff" : "#64748b",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: "pointer",
                marginBottom: 12,
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Active tab underline indicator */}
        <div style={{ height: 3, background:"#1e293b", position:"relative" }}>
          <div style={{ height:"100%", background:"#f97316", width:"100%", borderRadius:"2px 2px 0 0" }} />
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "32px 20px 0" }}>
        <div
          key={activeTab}
          className="legal-fade"
          style={{
            background: "#111",
            border: "1px solid #1e293b",
            borderRadius: 16,
            padding: "28px 28px 32px",
          }}
        >
          <Content />
        </div>

        {/* ── Quick nav to other policies ── */}
        <div style={{ marginTop: 24, marginBottom: 8 }}>
          <p style={{ color:"#334155", fontSize:12, marginBottom:12 }}>Other policies:</p>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {TABS.filter(t => t.id !== activeTab).map(t => (
              <button
                key={t.id}
                onClick={() => nav(t.path)}
                style={{
                  padding: "7px 14px",
                  background: "transparent",
                  border: "1px solid #1e293b",
                  borderRadius: 8,
                  color: "#64748b",
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "border-color .2s, color .2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor="#f97316"; e.currentTarget.style.color="#f97316"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor="#1e293b"; e.currentTarget.style.color="#64748b"; }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
